import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getIn, useFormikContext } from 'formik';
import type { FormikValues } from 'formik/dist/types';
import { every, set } from 'lodash';
import * as Yup from 'yup';

import CheckboxFormik from '~/components/CheckboxFormik';
import { useClaim } from '~/components/ClaimContainer';
import { AU_TFN_TAB_KEY, FORMIK_AU_TFN_FIELD_IDS } from '~/components/Contacts/UpsertContact/Tabs/AuTfn/constants';
import TfnDeclarationDocument from '~/components/Contacts/UpsertContact/Tabs/AuTfn/TfnDeclarationDocument';
import TfnVerificationBanner from '~/components/Contacts/UpsertContact/Tabs/AuTfn/TfnVerificationBanner';
import { getFullAuTfnPath } from '~/components/Contacts/UpsertContact/Tabs/AuTfn/utils';
import type { Tab } from '~/components/Contacts/UpsertContact/types';
import {
  getDocumentInitialValues,
  getDocumentPayload,
  getDocumentValidationSchema,
} from '~/components/Contacts/UpsertContact/UploadDocument/utils';
import { useContact } from '~/components/Contacts/UpsertContact/useContact';
import { getInitializedValues } from '~/components/Contacts/UpsertContact/utils';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { useCms } from '~/components/hooks/useCms';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import { TextFieldFormik } from '~/components/TextFieldFormik';
import useDataFetcher from '~/components/useDataFetcher';
import AU_EMPLOYMENT_BASIS_TYPES from '~/server_shared/generated-types/AU_EMPLOYMENT_BASIS_TYPES';
import AU_RESIDENCY_STATUS_TYPES from '~/server_shared/generated-types/AU_RESIDENCY_STATUS_TYPES';
import AU_TFN_EXEMPTION_TYPES from '~/server_shared/generated-types/AU_TFN_EXEMPTION_TYPES';
import type { TfnVerificationDetailsResponse } from '~/server_shared/openapi/data-contracts';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { reportAxiosError } from '~/Utils';

import TabWrapper from '../TabWrapper';

const AuTfnTab: React.FC = () => {
  const { isSubmitting, values, setFieldValue } = useFormikContext();
  const { documentUploadPercent, isCreation, originalContact } = useAuTfnTab();
  const { user } = useCms();
  const { isRequireVerificationForContactAuTfnEdit } = usePaymentsConfiguration();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  // Derive verification status from contact fields
  const tfnChangeUlid = originalContact?.au_tfn_change_ulid;

  // Fetch verification details when we have a change_ulid
  const {
    data: verificationDetails,
    reloadData: reloadVerificationDetails,
    isLoading: isLoadingDetails,
  } = useDataFetcher<TfnVerificationDetailsResponse>(
    tfnChangeUlid && isRequireVerificationForContactAuTfnEdit && !isCreation
      ? `/api/v1/contact_tfn_verification/${tfnChangeUlid}`
      : null,
    null,
    { shouldReportAxiosError: false }
  );

  const isApproved = (originalContact?.is_au_tfn_verified ?? false) || verificationDetails?.status === 'approved';
  // Only show pending if we're loading (safe default) or status is actually 'pending'.
  // Note: 'declined' status shouldn't normally be returned here because when a verification
  // is superseded, the contact's au_tfn_change_ulid is updated to point to the new pending
  // verification. A declined status would only occur with stale contact data, in which case
  // we show no banner rather than a broken "Awaiting approval" banner.
  const hasPendingVerification = Boolean(
    tfnChangeUlid && !isApproved && (isLoadingDetails || verificationDetails?.status === 'pending')
  );

  const handleApprove = async () => {
    if (!tfnChangeUlid) return;

    setIsApproving(true);
    try {
      await axios.post(`/api/v1/contact_tfn_verification/approve/${tfnChangeUlid}`);
      await reloadVerificationDetails();
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  // Reset edit mode when verification changes (e.g., after save creates new verification)
  useEffect(() => {
    setIsEditMode(false);
  }, [tfnChangeUlid]);

  const isFormDisabled =
    isRequireVerificationForContactAuTfnEdit && (hasPendingVerification || isApproved) && !isEditMode;

  // Check if this is a self-approval attempt (user trying to approve their own request)
  // Also treat as self-request while loading to prevent brief enabled state
  const isSelfRequest =
    hasPendingVerification && (isLoadingDetails || verificationDetails?.requesting_user_id === user.id);

  const sharedProps = {
    disabled: isSubmitting || isFormDisabled,
  };

  const showBanner =
    isRequireVerificationForContactAuTfnEdit && !isCreation && (hasPendingVerification || isApproved) && !isEditMode;

  const tfnValue = getIn(values, getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.TFN));
  const exemptionValue = getIn(values, getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.TFN_EXEMPTION));

  const handleTfnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFieldValue(getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.TFN), newValue);
    // Clear exemption if TFN is provided
    if (newValue) {
      setFieldValue(getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.TFN_EXEMPTION), '');
    }
  };

  const handleExemptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setFieldValue(getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.TFN_EXEMPTION), newValue);
    // Clear TFN if exemption is selected
    if (newValue) {
      setFieldValue(getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.TFN), '');
    }
  };

  return (
    <TabWrapper>
      <div className="mb-32 grid grid-cols-2 gap-20">
        {showBanner && (
          <div className="col-span-full">
            <TfnVerificationBanner
              hasPendingVerification={hasPendingVerification}
              isApproved={isApproved}
              requestingUser={verificationDetails?.requesting_user ?? null}
              requestDateTime={verificationDetails?.request_datetime ?? null}
              approvingUser={verificationDetails?.deciding_user ?? null}
              approvalDateTime={verificationDetails?.decision_datetime ?? null}
              onApprove={handleApprove}
              onEdit={handleEdit}
              isApproving={isApproving}
              isSelfRequest={isSelfRequest}
            />
          </div>
        )}
        <TextFieldFormik
          id={getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.TFN)}
          label="Tax File Number"
          fullWidth
          {...sharedProps}
          disabled={sharedProps.disabled || Boolean(exemptionValue)}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - TextFieldFormik not yet typed
          onChange={handleTfnChange}
        />
        <TextFieldFormik
          id={getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.TFN_EXEMPTION)}
          label="TFN Exemption"
          fullWidth
          select
          {...sharedProps}
          disabled={sharedProps.disabled || Boolean(tfnValue)}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - TextFieldFormik not yet typed
          onChange={handleExemptionChange}
        >
          {/* @ts-expect-error - TextFieldFormik not yet typed and its type inference is flawed */}
          {[
            <MenuItem key="-- Not Selected --" value="">
              -- Not Selected --
            </MenuItem>,
            ...Object.entries(AU_TFN_EXEMPTION_TYPES).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value.desc}
              </MenuItem>
            )),
          ]}
        </TextFieldFormik>
        <TextFieldFormik
          id={getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.EMPLOYMENT_BASIS)}
          label="Employment Basis"
          fullWidth
          select
          {...sharedProps}
        >
          {/* @ts-expect-error - TextFieldFormik not yet typed and its type inference is flawed */}
          {[
            <MenuItem key="-- Not Selected --" value="">
              -- Not Selected --
            </MenuItem>,
            ...Object.entries(AU_EMPLOYMENT_BASIS_TYPES).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value.desc}
              </MenuItem>
            )),
          ]}
        </TextFieldFormik>
        <TextFieldFormik
          id={getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.RESIDENCY_STATUS)}
          label="Residency Status"
          fullWidth
          select
          {...sharedProps}
        >
          {/* @ts-expect-error - TextFieldFormik not yet typed and its type inference is flawed */}
          {[
            <MenuItem key="-- Not Selected --" value="">
              -- Not Selected --
            </MenuItem>,
            ...Object.entries(AU_RESIDENCY_STATUS_TYPES).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value.desc}
              </MenuItem>
            )),
          ]}
        </TextFieldFormik>
        <CheckboxFormik
          id={getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.HAS_TAX_FREE_THRESHOLD)}
          label="Claiming tax-free threshold"
          {...sharedProps}
        />
        <CheckboxFormik
          id={getFullAuTfnPath(FORMIK_AU_TFN_FIELD_IDS.HAS_HELP_DEBT)}
          label="Has HELP/VSL/FS/SSL/TSL debt"
          {...sharedProps}
        />
        <TfnDeclarationDocument {...sharedProps} documentUploadPercent={documentUploadPercent} />
      </div>
    </TabWrapper>
  );
};

const useAuTfnTab = (): Tab & { isCreation: boolean; originalContact: ContactFullModel | undefined } => {
  // No Formik Context
  const [documentUploadPercent, setDocumentUploadPercent] = useState(0);
  const { currentCountry, originalContact, contactId, isCreation } = useContact();
  const { claim } = useClaim();
  const { userOrganization } = useCms();

  const getInitialValues = (contact?: ContactFullModel) => {
    const values = {};

    const setInitialValue = (fieldId: string, initialValue: unknown, defaultInitialValue: unknown = '') =>
      set(values, fieldId, initialValue ?? defaultInitialValue);

    const documentInitialValues = getDocumentInitialValues(FORMIK_AU_TFN_FIELD_IDS.TFN_DOCUMENT_ID, claim);
    every(Object.keys(documentInitialValues), (formikFieldId) =>
      setInitialValue(formikFieldId, documentInitialValues[formikFieldId])
    );

    setInitialValue(FORMIK_AU_TFN_FIELD_IDS.TFN, contact?.au_tfn);
    setInitialValue(FORMIK_AU_TFN_FIELD_IDS.TFN_EXEMPTION, contact?.au_tfn_exemption);
    setInitialValue(FORMIK_AU_TFN_FIELD_IDS.EMPLOYMENT_BASIS, contact?.au_employment_basis);
    setInitialValue(FORMIK_AU_TFN_FIELD_IDS.RESIDENCY_STATUS, contact?.au_residency_status);
    setInitialValue(FORMIK_AU_TFN_FIELD_IDS.HAS_TAX_FREE_THRESHOLD, contact?.au_has_tax_free_threshold, false);
    setInitialValue(FORMIK_AU_TFN_FIELD_IDS.HAS_HELP_DEBT, contact?.au_has_help_debt, false);
    setInitialValue(FORMIK_AU_TFN_FIELD_IDS.REMOVE_TFN_DOCUMENT, false);

    return values;
  };

  const getValuesToCompare = (values: FormikValues) => {
    return getInitializedValues(values);
  };

  const getValidationSchema = () => {
    const validations = {};

    const addValidation = (fieldId: string, fieldValidation?: Yup.AnySchema) =>
      set(validations, fieldId, fieldValidation);

    const documentValidationSchema = getDocumentValidationSchema(FORMIK_AU_TFN_FIELD_IDS.TFN_DOCUMENT_ID, true, claim);
    every(Object.keys(documentValidationSchema), (formikFieldId) =>
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      addValidation(formikFieldId, documentValidationSchema[formikFieldId])
    );

    addValidation(
      FORMIK_AU_TFN_FIELD_IDS.TFN,
      Yup.string().matches(/^\d{9}$/, { message: 'TFN must be exactly 9 digits', excludeEmptyString: true })
    );
    addValidation(FORMIK_AU_TFN_FIELD_IDS.TFN_EXEMPTION, Yup.string());
    addValidation(FORMIK_AU_TFN_FIELD_IDS.EMPLOYMENT_BASIS, Yup.string());
    addValidation(FORMIK_AU_TFN_FIELD_IDS.RESIDENCY_STATUS, Yup.string());
    addValidation(FORMIK_AU_TFN_FIELD_IDS.HAS_TAX_FREE_THRESHOLD, Yup.bool());
    addValidation(FORMIK_AU_TFN_FIELD_IDS.HAS_HELP_DEBT, Yup.bool());

    return validations;
  };

  const getPreparedPayload = async (values: FormikValues) => {
    return await getDocumentPayload(values, userOrganization.id, contactId, setDocumentUploadPercent);
  };

  const isHiddenForContact = () => {
    // Tab visible only for AU contacts
    return currentCountry !== 'AU';
  };

  return {
    label: 'TFN',
    tabKey: AU_TFN_TAB_KEY,
    tabComponent: AuTfnTab,
    getInitialValues,
    getValidationSchema,
    getValuesToCompare,
    isHiddenForContact,
    documentUploadPercent,
    getPreparedPayload,
    isCreation,
    originalContact,
  };
};

export default useAuTfnTab;
